import React, { FC } from 'react';
import { useQuery } from 'react-query';
import { queryKey } from 'constants/queryKey';
import PageApi from 'api/page';
import Skeleton from 'react-loading-skeleton';
import { ListingsApi } from 'app/api/listings';
import { useUser } from 'modules/user/userSelector';
import { TimeInput } from '@kontentino/ui';
import EditProfileTimezoneModal from 'app/modules/posts/components/EditProfileTimezoneModal';
import useBoolean from 'utils/hooks/useBoolean';
import { useTranslation } from 'react-i18next';

type Props = {
  pageId: number;
  onChange: (time: string | null) => void;
  value: string | null;
  disabled?: boolean;
};

const PostComposerPlanningContentTimeInput: FC<Props> = ({
  pageId,
  onChange,
  value,
  disabled,
}) => {
  const user = useUser();
  const pageTimezone = useQuery(queryKey.pageTimezone(pageId), async () => {
    const [page, timezones] = await Promise.all([
      PageApi.detail(pageId),
      ListingsApi.timezones(),
    ]);

    return timezones.record[page.timezone] ?? page.timezone;
  });
  const [isTimezoneModalOpen, setIsTimezoneModalOpen] = useBoolean(false);
  const { t } = useTranslation();

  return (
    <>
      <TimeInput
        onChange={onChange}
        value={value}
        disabled={disabled}
        dataName={{
          timeInput: 'posts-composer-set-time',
        }}
        dataCy={{
          timeInput: 'post-form-time-picker',
          emptyLabel: 'post-form-time-picker-empty',
        }}
        is12HourFormat={!user.useMeridianTime}
      />
      {pageTimezone.isLoading && <Skeleton height={16} />}
      {pageTimezone.data && (
        <span className="tw-text-sm">
          <span className="tw-text-grayscale-100">
            {`${pageTimezone.data} `}
          </span>
          {!disabled && (
            <span
              className="tw-cursor-pointer tw-font-medium tw-text-primary-100 hover:tw-text-primary-120"
              onClick={setIsTimezoneModalOpen.toggle}
            >
              {t('edit')}
            </span>
          )}
        </span>
      )}
      {pageId && (
        <EditProfileTimezoneModal
          pageId={pageId}
          isOpen={isTimezoneModalOpen}
          onClose={setIsTimezoneModalOpen.toggle}
        />
      )}
    </>
  );
};

export default PostComposerPlanningContentTimeInput;
